import { langTagsEqual, langTagsRelated } from '../lang.ts';
import type { SubtitleTrackRef } from '../model/media.ts';
import type { AudioLanguagePref, CodecFamily, SubtitlePref, VideoQualityPref } from '../model/playback.ts';
import {
  hasRole,
  isDrcTrack,
  isDubTrack,
  ROLE_MAIN,
  subtitleTrackKey,
  type AudioTrackInfo,
  type VideoTrackInfo,
} from '../model/tracks.ts';

/**
 * R5, the second half: core states the *preference*, and this file resolves it against whatever the
 * manifest actually turned out to offer. Pure, total, and engine-free — the web adapter feeds it
 * `getVideoTracks()`, the Android adapter feeds it Media3 formats, and both get the same answer.
 *
 * Every decision comes back as a **stable key plus a reason**. The reason is not decoration: R6's
 * recovery has to re-apply preferences after a reload, and "the German track is simply not in this
 * manifest" must be distinguishable from "the German track is what is playing". Silently degrading
 * to a default is the failure mode §7 calls out by name.
 */

export type SelectionReason =
  /** The requested thing exists and was chosen. */
  | 'exact'
  /** Requested height absent; the closest one **below** it was chosen. */
  | 'nearest-lower'
  /** Requested height absent and nothing below it; the closest above was chosen. */
  | 'nearest-higher'
  /** No preference expressed — the engine's adaptive logic keeps control. */
  | 'adaptive'
  /** `auto` audio resolved to the original (`main`, not `dub`, not DRC). */
  | 'original'
  /** Nothing suitable existed at all. `trackKey` is `null`; do not fall back silently. */
  | 'unavailable'
  /** The preference asked for nothing (subtitles off). */
  | 'off';

export interface VideoDecision {
  /**
   * §7's ordering matters: ABR must be turned **off before** a track is selected, or Shaka's next
   * adaptation cycle overrides the choice a second later.
   */
  readonly abrEnabled: boolean;
  /** `null` when ABR keeps control. */
  readonly trackKey: string | null;
  /** Fed to `restrictions.maxHeight` (web) / `setMaxVideoSize` (Media3). `null` = unrestricted. */
  readonly maxHeight: number | null;
  readonly reason: SelectionReason;
  /**
   * `false` when a codec family was asked for and this manifest does not expose it — orthogonal to
   * `reason`, which is about height. On web that is the signal to **reload** with a different
   * `preferredVideo` codec rather than to keep selecting: Shaka drops the losing family at manifest
   * filter time, so no amount of `selectVideoTrack` will bring H.264 back (verified in 0d: 6 AV1
   * tracks exposed out of 12 representations).
   */
  readonly codecFamilyMatched: boolean;
}

export interface AudioDecision {
  readonly trackKey: string | null;
  readonly reason: SelectionReason;
}

export interface SubtitleDecision {
  readonly track: SubtitleTrackRef | null;
  readonly trackKey: string | null;
  readonly reason: SelectionReason;
}

// ---------------------------------------------------------------------------
// Video
// ---------------------------------------------------------------------------

function heightOf(track: VideoTrackInfo): number {
  return track.height ?? 0;
}

/** Deterministic order: tallest first, then fattest. Two tracks never tie in this corpus, but a
 *  selector whose answer depends on manifest order is a selector that will differ on Android. */
function byQualityDesc(a: VideoTrackInfo, b: VideoTrackInfo): number {
  return heightOf(b) - heightOf(a) || b.bandwidth - a.bandwidth;
}

/** "Does this filtered manifest expose the family that was asked for?" No preference → yes. */
function codecFamilyPresent(
  family: CodecFamily | undefined,
  tracks: readonly VideoTrackInfo[],
): boolean {
  return family === undefined || tracks.some((track) => track.codecFamily === family);
}

export function decideVideoTrack(
  pref: VideoQualityPref,
  tracks: readonly VideoTrackInfo[],
): VideoDecision {
  if (pref.kind === 'auto') {
    return {
      abrEnabled: true,
      trackKey: null,
      maxHeight: pref.maxHeight ?? null,
      reason: 'adaptive',
      // "Force H.264" without pinning a height. The answer is about the *manifest as filtered*, so
      // an empty track list reads as unmatched rather than as matched-by-vacuous-truth: before the
      // reload that changes the codec there is nothing to be matched against.
      codecFamilyMatched: codecFamilyPresent(pref.codec, tracks),
    };
  }

  if (tracks.length === 0) {
    return {
      abrEnabled: true,
      trackKey: null,
      maxHeight: null,
      reason: 'unavailable',
      codecFamilyMatched: false,
    };
  }

  // A codec-pinned preference against a track list of the *other* family is the normal state
  // between "user picked Force H.264" and "the reload landed", not an error. Pin the height anyway
  // so the picture does not drop to ABR in the meantime, and report the mismatch separately.
  const family = pref.codec;
  const inFamily = family === undefined ? [...tracks] : tracks.filter((t) => t.codecFamily === family);
  const codecFamilyMatched = codecFamilyPresent(family, tracks);
  const pool = (codecFamilyMatched ? inFamily : [...tracks]).sort(byQualityDesc);

  const exact = pool.find((t) => heightOf(t) === pref.height);
  if (exact) {
    return { abrEnabled: false, trackKey: exact.key, maxHeight: null, reason: 'exact', codecFamilyMatched };
  }

  const lower = pool.find((t) => heightOf(t) < pref.height);
  if (lower) {
    return {
      abrEnabled: false,
      trackKey: lower.key,
      maxHeight: null,
      reason: 'nearest-lower',
      codecFamilyMatched,
    };
  }

  // Everything is taller than asked for: take the smallest of them.
  const higher = pool[pool.length - 1];
  if (higher === undefined) {
    return {
      abrEnabled: true,
      trackKey: null,
      maxHeight: null,
      reason: 'unavailable',
      codecFamilyMatched,
    };
  }
  return {
    abrEnabled: false,
    trackKey: higher.key,
    maxHeight: null,
    reason: 'nearest-higher',
    codecFamilyMatched,
  };
}

export interface QualityOption {
  readonly key: string;
  readonly height: number;
  readonly codecFamily: CodecFamily;
  readonly bandwidth: number;
}

/** Menu source: one entry per (height, codec family), tallest first. */
export function qualityOptions(tracks: readonly VideoTrackInfo[]): readonly QualityOption[] {
  const options: QualityOption[] = [];
  for (const track of [...tracks].sort(byQualityDesc)) {
    if (track.height === null) continue;
    if (options.some((option) => option.key === track.key)) continue;
    options.push({
      key: track.key,
      height: track.height,
      codecFamily: track.codecFamily,
      bandwidth: track.bandwidth,
    });
  }
  return options;
}

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------

/**
 * Ranks candidates so the *best* one is first. Applied after language filtering, and it is what
 * keeps `auto` off both the Arabic dub (alphabetically first in the MPD, which is why Shaka's own
 * default lands there — §2) and the DRC "Stable Volume" duplicate.
 */
function rankAudio(track: AudioTrackInfo, wantDrc: boolean): number {
  let score = 0;
  // Not a dub outranks everything: `auto` means *the original*, and no dynamics preference is worth
  // serving a different language than the one the uploader recorded.
  if (!isDubTrack(track)) score += 4;
  /*
   * Then the rendition that was asked for — and it has to outrank `main`, which is the whole of what
   * "Use stable volume" being a switch depends on (measured 2026-08-02).
   *
   * The DRC set is a *duplicate* of every language rather than an extra one, but the manifests do not
   * label it consistently. On a multi-language video the twin carries `main` **as well**
   * (`aTVeC-GyKWQ`: `English (US) original (Stable Volume)`, roles `main` + DRC), and there a `main`
   * bonus above this one still lands correctly by the tie-break. On a **single-language** video the
   * twin carries `alternate` + DRC and no `main` at all (`kC5oO7tzphg`, `jNQXAC9IVRw`: labels
   * `Original` and `Stable Volume`) — so with `main` ranked above the rendition, the switch resolved
   * straight back to the plain track and did visibly nothing on exactly the videos where it is the
   * only choice the menu offers.
   */
  if (isDrcTrack(track) === wantDrc) score += 2;
  if (hasRole(track.roles, ROLE_MAIN)) score += 1;
  return score;
}

function bestAudio(
  candidates: readonly AudioTrackInfo[],
  wantDrc = false,
): AudioTrackInfo | null {
  let best: AudioTrackInfo | null = null;
  let bestScore = -1;
  for (const track of candidates) {
    const score = rankAudio(track, wantDrc);
    // Strictly greater, so a genuine tie resolves to manifest order and the answer is reproducible.
    // The English pair in the captured MPD is not a tie: without DRC wanted "English original" scores
    // 7 and "English original (Stable Volume)" 5, and with it wanted the two swap.
    if (score > bestScore) {
      best = track;
      bestScore = score;
    }
  }
  return best;
}

export function decideAudioTrack(
  pref: AudioLanguagePref,
  tracks: readonly AudioTrackInfo[],
): AudioDecision {
  if (tracks.length === 0) return { trackKey: null, reason: 'unavailable' };

  // `drc` rides on both arms: it asks for the DRC rendition of whatever the language half resolves
  // to, so `auto` with it means the *original's* Stable Volume variant (the settings toggle turned
  // it from a per-row choice into a mode).
  const wantDrc = pref.drc === true;

  if (pref.kind === 'auto') {
    // §7: never auto-select by browser locale. `auto` means *the original*, whatever language that
    // happens to be — `Accept-Language` is not consent to be served a dub.
    const best = bestAudio(tracks, wantDrc);
    return best === null
      ? { trackKey: null, reason: 'unavailable' }
      : { trackKey: best.key, reason: 'original' };
  }

  const allowed = pref.allowDub ? tracks : tracks.filter((t) => !isDubTrack(t));

  const exact = allowed.filter((t) => langTagsEqual(t.language, pref.code));
  const exactBest = bestAudio(exact, wantDrc);
  if (exactBest !== null) return { trackKey: exactBest.key, reason: 'exact' };

  // `de` asked for, `de-DE` offered (or the reverse). Same language, different tag depth.
  const related = allowed.filter((t) => langTagsRelated(t.language, pref.code));
  const relatedBest = bestAudio(related, wantDrc);
  if (relatedBest !== null) return { trackKey: relatedBest.key, reason: 'nearest-lower' };

  return { trackKey: null, reason: 'unavailable' };
}

/**
 * Menu source: **one track per language**, never both renditions (§7). The DRC "Stable Volume" set
 * duplicates languages rather than adding any, so a menu that listed both would double its rows for
 * a distinction the stable-volume toggle already states. `stableVolume` picks which rendition each
 * language's row *is* — falling back to the one that exists when a language has no twin, because a
 * missing rendition must not make the language itself unofferable.
 */
export function audioOptions(
  tracks: readonly AudioTrackInfo[],
  options: { readonly stableVolume: boolean } = { stableVolume: false },
): readonly AudioTrackInfo[] {
  const want = options.stableVolume;
  const byLanguage = new Map<string, AudioTrackInfo>();
  for (const track of tracks) {
    const language = track.language.toLowerCase();
    const kept = byLanguage.get(language);
    if (kept === undefined) {
      byLanguage.set(language, track);
    } else if (isDrcTrack(kept) !== want && isDrcTrack(track) === want) {
      byLanguage.set(language, track);
    }
  }
  return [...byLanguage.values()];
}

// ---------------------------------------------------------------------------
// Subtitles
// ---------------------------------------------------------------------------

function subtitleRank(track: SubtitleTrackRef): number {
  return track.autoGenerated ? 0 : 1;
}

export function decideSubtitle(
  pref: SubtitlePref,
  tracks: readonly SubtitleTrackRef[],
): SubtitleDecision {
  if (pref.kind === 'off') return { track: null, trackKey: null, reason: 'off' };

  const allowed = pref.allowAutoGenerated ? tracks : tracks.filter((t) => !t.autoGenerated);

  const pick = (candidates: readonly SubtitleTrackRef[]): SubtitleTrackRef | null => {
    let best: SubtitleTrackRef | null = null;
    for (const track of candidates) {
      // §2: `en` appears **twice** on a video with auto-generated captions. When both are on the
      // table the human-authored one wins; only the label tells them apart.
      if (best === null || subtitleRank(track) > subtitleRank(best)) best = track;
    }
    return best;
  };

  const exact = pick(allowed.filter((t) => langTagsEqual(t.languageCode, pref.code)));
  if (exact !== null) {
    return { track: exact, trackKey: subtitleTrackKey(exact.languageCode, exact.label), reason: 'exact' };
  }

  // `de` asked for, `de-DE` offered — the caption list uses region-qualified tags where the audio
  // adaptation sets do not.
  const related = pick(allowed.filter((t) => langTagsRelated(t.languageCode, pref.code)));
  if (related !== null) {
    return {
      track: related,
      trackKey: subtitleTrackKey(related.languageCode, related.label),
      reason: 'nearest-lower',
    };
  }

  return { track: null, trackKey: null, reason: 'unavailable' };
}

export function subtitleOptions(
  tracks: readonly SubtitleTrackRef[],
  options: { readonly includeAutoGenerated: boolean } = { includeAutoGenerated: true },
): readonly SubtitleTrackRef[] {
  return options.includeAutoGenerated ? tracks : tracks.filter((track) => !track.autoGenerated);
}

// ---------------------------------------------------------------------------
// Playback rate
// ---------------------------------------------------------------------------

/** §7 — `video.playbackRate`, not `trickPlay()`, which mutes audio. */
export const PLAYBACK_RATES: readonly number[] = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/** Snaps to the nearest offered rate, so a persisted value from another build can never stick. */
export function clampPlaybackRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  let best = 1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of PLAYBACK_RATES) {
    const distance = Math.abs(candidate - rate);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * §7's `<` and `>`: move along the ladder rather than by a fixed multiplier, so the steps are the
 * same ones the menu offers and pressing `>` five times cannot land on a rate that is not on it.
 * Saturates at both ends — wrapping from 2× back to 0.25× on one keypress is a jump scare.
 */
export function stepPlaybackRate(rate: number, steps: number): number {
  const current = PLAYBACK_RATES.indexOf(clampPlaybackRate(rate));
  const next = Math.min(PLAYBACK_RATES.length - 1, Math.max(0, current + Math.trunc(steps)));
  return PLAYBACK_RATES[next] ?? 1;
}
